import React, { useState } from 'react';
import style from './message-resp.module.scss';
import AddIcon from '../../../../../../../../assets/svg/solo-chat/circle-plus.svg';
import { ShuffleIcon, DeleteIcon } from '../../../../icons';
import EmptyIcon from '../../../../../../../../assets/svg/solo-chat/qna-messages.svg';
import { AddMessage } from './AddMessage';
import { useQuery, useAppAndBranchId } from '../../../../hooks';
import { api } from '../../../content/api';
import { PreviewContent } from './PreviewContent';
import { labelMap } from '../../../../../soloJourney/properties/message/components';
import { Icon, Spin } from 'antd'

export type MessageRespAction = {
	field: 'messages' | 'suggestions' | 'isRandom';
} & (
	| {
			op: 'edit';
			payload: {
				index: number;
				value: string;
			};
	  }
	| {
			op: 'add';
			payload: string;
	  }
	| {
			op: 'toggle';
			payload: boolean;
	  }
	| {
			op: 'delete';
			payload: number;
	  }
);

type Props = {
	messages: string[];
	isRandom: boolean;
	suggestions?: string[];
	trigger: (action: MessageRespAction) => void;
};

export function MessageResp(props: Props) {
	const { messages, suggestions, isRandom, trigger } = props;
	const [selectedTab, setSelectedTab] = useState<'suggestions' | 'messages'>(
		'messages',
	);
	const [isAddMessageVisible, setIsAddMessageVisible] = useState(false);

	const styles: any = {
		'--select-bg': '#F4F5F5',
		'--select-width': '94px',
	};

	const showAddSuggestions = !(selectedTab === "suggestions" && !suggestions?.length)
	const shodAddMessages = !(selectedTab === "messages" && (!messages.length || isAddMessageVisible))

	return (
		<div className={style.messageResp} style={styles}>
			<div className={style.header}>
				<div className={style.tabs}>
					<button
						data-selected={selectedTab === 'messages'}
						onClick={() => setSelectedTab('messages')}
					>
						Messages
					</button>
					<button
						data-selected={selectedTab === 'suggestions'}
						onClick={() => setSelectedTab('suggestions')}
					>
						Suggestions
					</button>
					<div
						data-selected-tab={
							selectedTab === 'suggestions' ? 'suggestions' : ''
						}
						className={style.tabHandle}
					/>
				</div>
				<div className={style.right}>
					<button
						title="Random Messages"
						style={{ marginLeft: "auto" }}
						onClick={() => {
							trigger({
								field: 'isRandom',
								op: 'toggle',
								payload: !isRandom,
							});
						}}
					>
						<ShuffleIcon
							width="18"
							height="18"
							stroke={isRandom ? '#F1722E' : '#8C9099'}
						/>
					</button>
					{shodAddMessages && showAddSuggestions && (
					 <>
						<div className={style.line} />
						<button
							className={style.add}
							onClick={() => {
								if (selectedTab === 'suggestions') {
									trigger({
										field: 'suggestions',
										op: 'add',
										payload: '',
									});
								} else {
									setIsAddMessageVisible(true);
								}
							}}
						>
							<img src={AddIcon} alt="add" />
							<span>
								Add {selectedTab === 'messages' ? 'Message' : 'Suggestion'}
							</span>
						</button>
					 </>
					)}
				</div>
			</div>
			{selectedTab === 'messages' ? (
				<Messages
					trigger={trigger}
					messages={messages}
					isAddMessageVisible={isAddMessageVisible}
					closeAddMessage={() => setIsAddMessageVisible(false)}
					showAddMessage={() => setIsAddMessageVisible(true)}
				/>
			) : (
				<Suggestions
					trigger={trigger}
					suggestions={suggestions ?? []}
					showAddMessage={() => setIsAddMessageVisible(true)}
				/>
			)}
		</div>
	);
}

type HandlerProps = {
	showAddMessage: () => void;
	trigger: (action: MessageRespAction) => void;
};

type MessagesProps = {
	messages: string[];
	isAddMessageVisible: boolean;
	closeAddMessage: () => void;
	showAddMessage: () => void;
} & HandlerProps;

function Messages(props: MessagesProps) {
	const {
		messages,
		isAddMessageVisible,
		closeAddMessage,
		showAddMessage,
		trigger,
	} = props;
	const [appId, branchId] = useAppAndBranchId();
	const { data: contents, loading } = useQuery(
		() => api.getChatContentsByIds(appId, branchId, messages),
		messages.join(","),
		messages.length > 0,
	);

	if (!messages.length && !isAddMessageVisible) {
		return (
			<EmptyState
				trigger={trigger}
				showAddMessage={showAddMessage}
				type="message"
			/>
		);
	}

	const styles: any = {
		'--select-width': '130px',
	};

	return (
		<div className={style.messages}>
			<div style={styles}>
				{isAddMessageVisible && (
					<AddMessage selectedContentIds={messages} trigger={trigger} closeAddMessage={closeAddMessage} />
				)}
			</div>
      {loading && (
				<Spin indicator={<Icon type="loading" style={{ fontSize: 26, color: '#15808C' }} spin />} />
      )}
			{!loading && (
				<div className={style.contents}>
					{messages.map((contentId: string, i: number) => {
						const content = contents?.find((el) => el.contentId === contentId);
						if (!content) {
							return null;
						}
						return (
							<PreviewContent
								contentId={content.contentId}
								contentType={content.contentType}
								key={content.id}
								contentValue={{ ...content.contentValue, id: content.id }}
								remove={() => {
                  trigger({
                    field: "messages",
                    payload: i,
                    op: 'delete',
                  })
                }}
								title={
									(content.contentValue as any)[labelMap[content.contentType]]
								}
							/>
						);
					})}
				</div>
			)}
		</div>
	);
}

function Suggestions(props: { suggestions: string[] } & HandlerProps) {
	const { suggestions, showAddMessage, trigger } = props;

	if (!suggestions.length) {
		return (
			<EmptyState
				trigger={trigger}
				showAddMessage={showAddMessage}
				type="suggestion"
			/>
		);
	}

	return (
		<div className={style.suggestions}>
			{suggestions.map((suggestion, i) => (
				<Suggestion key={i} index={i} suggestion={suggestion} {...props} />
			))}
		</div>
	);
}

function Suggestion(
	props: { suggestion: string; index: number } & HandlerProps,
) {
	return (
		<div className={style.suggestion}>
			<textarea
				value={props.suggestion}
				onChange={(e) => {
					props.trigger({
						field: 'suggestions',
						op: 'edit',
						payload: {
							index: props.index,
							value: e.target.value,
						},
					});
				}}
			/>
			<button
				onClick={() => {
					props.trigger({
						field: 'suggestions',
						op: 'delete',
            payload: props.index,
					});
				}}
			>
				<DeleteIcon width="13" height="14" stroke="#D30000" />
				<span>Remove</span>
			</button>
			<hr style={{ borderTop: '1px dashed #BABFC8', marginTop: 12 }} />
		</div>
	);
}

type EmptyProps = {
	type: 'message' | 'suggestion';
	showAddMessage: () => void;
	trigger: (action: MessageRespAction) => void;
};

function EmptyState(props: EmptyProps) {
	const { type, showAddMessage, trigger } = props;
	return (
		<div className={style.empty}>
			<div>
				<img src={EmptyIcon} alt="empty" />
				<p>You have not added any {type} response yet</p>
				<button
					onClick={() => {
						if (type === 'suggestion') {
							trigger({
								payload: '',
								op: 'add',
								field: 'suggestions',
							});
						} else {
							showAddMessage();
						}
					}}
				>
					Add {type === 'message' ? 'Message' : 'Suggestion'}
				</button>
			</div>
		</div>
	);
}
